import moment from 'moment';
import { get, isEmpty, values } from 'lodash/fp';

import {
  API_CALL_SUCCESS,
  SUBMIT_CHALLENGE_SUCCESS,
  SUBMIT_MULTIPLE_SUCCESS,
  SUBMIT_MULTIPLE_LINEUP_ENTRY_SUCCESS,
  DEPOSIT_SUCCESS,
  API_CALL_FAILURE
} from '../constants/actionTypes';
import {
  FACEBOOK_SIGN_IN,
  PXC_SIGN_IN,
  USER_SIGN_UP,
  USER_SIGN_IN,
  USER_UPDATE,
  LIMITS,
  USER_WITHDRAWAL,
  CREATE_CHALLENGE
} from '../constants/apiTypes';
import * as entryType from '../constants/entryTypes';

function getSubmissionType({ didAutofill, didMakeSelection, didLoadLineUp }) {
  if (didAutofill) {
    if (didMakeSelection) {
      return entryType.PARTIAL_AUTOFILL;
    }
    return entryType.AUTOFILL;
  }
  if (didLoadLineUp) {
    return entryType.LOAD_LINEUP;
  }
  return entryType.STANDARD;
}

function tagmanagerMiddleware() {
  return ({ getState }) => next => action => {
    const { settings, user } = getState();

    if (action.type === API_CALL_SUCCESS) {
      switch (action.api) {
        case USER_SIGN_UP:
          window.dataLayer.push({
            event: 'USER_SIGN_UP'
          });
          window.dataLayer.push({
            event: 'siteRegistrationCompleted',
            userId: action.data.id,
            ltv: 0
          });
          break;

        case FACEBOOK_SIGN_IN:
        case PXC_SIGN_IN:
          if (moment().diff(action.data.created_at) < 60000) {
            window.dataLayer.push({
              event: 'USER_SIGN_UP'
            });
            window.dataLayer.push({
              event: 'siteRegistrationCompleted',
              userId: action.data.id,
              ltv: 0
            });
          }
          break;

        case USER_SIGN_IN:
          window.dataLayer.push({
            event: 'loginSuccess',
            userId: action.data.id,
            ltv: 0
          });
          break;

        case USER_UPDATE:
          if (action.data && action.data.profile_completed) {
            window.dataLayer.push({
              event: 'profileComplete',
              userId: action.data.id,
              birthYear: new Date(action.data.birth_date).getFullYear().toString(),
              postCode: action.data.postcode
            });
          }
          break;

        case LIMITS:
          if (action.data) {
            if (action.data.duration !== 'undefined') {
              window.dataLayer.push({
                event: 'activityLimit',
                newLimitSet: action.data.duration
              });
            }
            if (action.data.deposit !== 'undefined') {
              window.dataLayer.push({
                event: 'depositLimit',
                newLimitSet: action.data.deposit
              });
            }
            if (action.data.spending !== 'undefined') {
              window.dataLayer.push({
                event: 'spendingLimit',
                newLimitSet: action.data.spending
              });
            }
          }
          break;

        case USER_WITHDRAWAL:
          window.dataLayer.push({
            event: 'purchase',
            ecommerce: {
              currencyCode: get('currency.code', settings),
              purchase: {
                actionField: {
                  id: moment()
                    .unix()
                    .toString(),
                  revenue: action.amount * -1
                },
                products: [
                  {
                    name: 'withdrawal',
                    id: get('currentUser.id', user),
                    price: action.amount * -1,
                    quantity: 1
                  }
                ]
              }
            }
          });
          break;

        case CREATE_CHALLENGE:
          try {
            window.dataLayer.push({
              event: 'contestCreated',
              sport: get('data[0].name', action),
              'entry cost':
                get('data[0].total_fee', action) / (get('meta.currency.divisor', action) || 1)
            });
          } catch (err) {
            // do nothing
          }
          break;

        default:
      }
    } else if (action.type === API_CALL_FAILURE) {
      switch (action.api) {
        case USER_SIGN_UP:
          window.dataLayer.push({
            event: 'siteRegistrationFailed',
            reason: Array.isArray(get('errors.messages', action))
              ? get('errors.messages', action)
                  .map(msg => msg.message)
                  .join('\n')
              : ''
          });
          break;

        case USER_SIGN_IN:
          window.dataLayer.push({
            event: 'loginFailed',
            reason: Array.isArray(get('errors.messages', action))
              ? get('errors.messages', action)
                  .map(msg => msg.message)
                  .join('\n')
              : ''
          });
          break;

        default:
      }
    } else if (action.type === SUBMIT_CHALLENGE_SUCCESS) {
      const { lineUp, challenge } = getState();

      window.dataLayer.push({
        event: 'CHALLENGE_SUBMISSION',
        submission_type: getSubmissionType(lineUp)
      });

      if (challenge) {
        const { cost, id, name, isRapidfire, isH2H, sportName } = challenge;
        let brand = cost === 0 ? 'Free' : 'Paid';

        if (isRapidfire) {
          brand = 'Rapidfire';
        } else if (isH2H) {
          brand = 'Head to head';
        }

        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            currencyCode: get('currency.code', settings),
            purchase: {
              actionField: {
                id: moment()
                  .unix()
                  .toString(),
                revenue: cost
              },
              products: [
                {
                  name,
                  id,
                  brand,
                  category: sportName,
                  price: cost,
                  quantity: 1
                }
              ]
            }
          }
        });
      }
    } else if (action.type === DEPOSIT_SUCCESS) {
      window.dataLayer.push({
        event: 'purchase',
        deposits: 1,
        ecommerce: {
          currencyCode: get('currency.code', settings),
          purchase: {
            actionField: {
              id: moment()
                .unix()
                .toString(),
              revenue: action.amount
            },
            products: [
              {
                name: 'deposit',
                id: get('currentUser.id', user),
                brand: action.paymentType,
                price: action.amount,
                quantity: 1
              }
            ]
          }
        }
      });
    } else if (action.type === SUBMIT_MULTIPLE_SUCCESS) {
      const {
        compatibleChallenges: { challenges }
      } = getState();
      const totalCost = action.totalCost ? action.totalCost : 0;
      const products = {};

      if (Array.isArray(action.payload)) {
        action.payload.forEach(challengeRecord => {
          const challenge = challenges.find(ch => ch.id === challengeRecord.challengeId);

          if (challenge && Array.isArray(challengeRecord.data)) {
            const { id, name, isRapidfire, isH2H, cost, sportName } = challenge;
            let brand = cost === 0 ? 'Free' : 'Paid';

            if (isRapidfire) {
              brand = 'Rapidfire';
            } else if (isH2H) {
              brand = 'Head to head';
            }

            if (get(id, products)) {
              products[id].quantity += challengeRecord.data.length;
            } else {
              products[id] = {
                name,
                id,
                brand,
                category: sportName,
                price: cost,
                quantity: challengeRecord.data.length
              };
            }
          }
        });

        if (!isEmpty(products)) {
          window.dataLayer.push({
            event: 'purchase',
            ecommerce: {
              currencyCode: get('currency.code', settings),
              purchase: {
                actionField: {
                  id: moment()
                    .unix()
                    .toString(),
                  revenue: totalCost
                },
                products: values(products)
              }
            }
          });
        }
      }
    } else if (action.type === SUBMIT_MULTIPLE_LINEUP_ENTRY_SUCCESS) {
      const { challenge } = getState();
      const totalCost = action.totalCost ? action.totalCost : 0;
      const products = {};

      if (Array.isArray(action.payload)) {
        action.payload.forEach(challengeRecord => {
          if (challenge && Array.isArray(challengeRecord.data)) {
            const { id, name, isRapidfire, isH2H, cost, sportName } = challenge;
            let brand = cost === 0 ? 'Free' : 'Paid';

            if (isRapidfire) {
              brand = 'Rapidfire';
            } else if (isH2H) {
              brand = 'Head to head';
            }

            if (get(id, products)) {
              products[id].quantity += challengeRecord.data.length;
            } else {
              products[id] = {
                name,
                id,
                brand,
                category: sportName,
                price: cost,
                quantity: challengeRecord.data.length
              };
            }
          }
        });

        if (!isEmpty(products)) {
          window.dataLayer.push({
            event: 'purchase',
            ecommerce: {
              currencyCode: get('currency.code', settings),
              purchase: {
                actionField: {
                  id: moment()
                    .unix()
                    .toString(),
                  revenue: totalCost
                },
                products: values(products)
              }
            }
          });
        }
      }
    }

    return next(action);
  };
}

export default tagmanagerMiddleware;
